"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { X, Download, Share2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { usePlatform } from "@/hooks/use-platform"

/**
 * PWA Install Prompt
 * 
 * Shows a prompt to install the app when:
 * - The app is installable (beforeinstallprompt event on Android/Chrome)
 * - iOS Safari: Shows manual instructions (beforeinstallprompt not supported)
 * - User hasn't dismissed it before
 * - Not already installed
 * - Only on mobile devices (since PWA installation is primarily for mobile)
 */
export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isIOSSafari, setIsIOSSafari] = useState(false)
  const { isMobile, isIOS, isNative, isStandalone, isLoaded } = usePlatform()

  // Detect iOS Safari
  useEffect(() => {
    const userAgent = navigator.userAgent
    // iOS detection from usePlatform is robust, but for the Share button instructions
    // we specifically want to know if it's the Safari browser (not a webview or app)
    const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS/.test(userAgent)
    setIsIOSSafari(isIOS && isSafari && !isStandalone && !isNative)
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[PWA Install] iOS Safari Detection:', { isIOS, isSafari, isStandalone, isNative, userAgent })
    }
  }, [isIOS, isStandalone, isNative])

  useEffect(() => {
    // Wait for platform detection to complete
    if (!isLoaded) {
      return
    }

    // Only proceed if we're on a mobile device and NOT already in an app environment
    // (Native apps and standalone PWAs don't need install prompts)
    // Also skip for iOS since we have a native app now
    if (!isMobile || isNative || isStandalone || isIOS) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[PWA Install] Not installable environment or iOS:', { isMobile, isNative, isStandalone, isIOS })
      }
      return
    }

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem("pwa-install-dismissed")
    if (dismissed) {
      return
    }

    // For iOS Safari, show manual instructions (beforeinstallprompt doesn't fire)
    if (isIOSSafari) {
      // Show prompt after a delay on iOS
      const timer = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[PWA Install] Showing iOS prompt')
        }
        setShowPrompt(true)
      }, 3000) // 3 seconds
      return () => clearTimeout(timer)
    }

    // For Android/Chrome, listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e)
      if (process.env.NODE_ENV === 'development') {
        console.log('[PWA Install] beforeinstallprompt event received')
      }
      // Show prompt after a delay
      setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[PWA Install] Showing Android/Chrome prompt')
        }
        setShowPrompt(true)
      }, 3000) // 3 seconds
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt)

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt)
    }
  }, [isMobile, isIOSSafari, isNative, isStandalone, isLoaded])

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return
    }

    // Show the install prompt
    deferredPrompt.prompt()

    // Wait for the user to respond
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === "accepted") {
      console.log("User accepted the install prompt")
    } else {
      console.log("User dismissed the install prompt")
    }

    // Clear the deferred prompt
    setDeferredPrompt(null)
    setShowPrompt(false)
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    localStorage.setItem("pwa-install-dismissed", "true")
  }

  // Don't show if not mobile, or already in app environment, or prompt conditions not met
  if (!isLoaded || !isMobile || isNative || isStandalone || !showPrompt) {
    return null
  }

  // For Android/Chrome, require deferredPrompt
  if (!isIOSSafari && !deferredPrompt) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[PWA Install] Waiting for beforeinstallprompt event')
    }
    return null
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('[PWA Install] Showing prompt:', { isMobile, isIOSSafari, hasDeferredPrompt: !!deferredPrompt })
  }

  return (
    <Dialog open={showPrompt} onOpenChange={setShowPrompt}>
      <DialogContent className="sm:max-w-[425px] bg-white dark:bg-slate-900 border shadow-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isIOSSafari ? (
              <Share2 className="h-5 w-5" />
            ) : (
              <Download className="h-5 w-5" />
            )}
            Install spots2surf
          </DialogTitle>
          <DialogDescription>
            {isIOSSafari ? (
              <>
                Install our app for a better experience! Tap the <Share2 className="h-4 w-4 inline" /> Share button 
                in Safari, then select &quot;Add to Home Screen&quot;.
              </>
            ) : (
              "Install our app for a better experience! Get quick access, offline support, and faster loading."
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row gap-2 pt-4">
          {isIOSSafari ? (
            <>
              <Button onClick={handleDismiss} className="flex-1">
                Got it!
              </Button>
              <Button variant="outline" onClick={handleDismiss} className="flex-1">
                <X className="h-4 w-4 mr-2" />
                Maybe Later
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleInstall} className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Install App
              </Button>
              <Button variant="outline" onClick={handleDismiss} className="flex-1">
                <X className="h-4 w-4 mr-2" />
                Maybe Later
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

